import { useState } from "react";

import { toErrorMessage } from "../../../../components/Input/InstrumentSearch";
import { AdminJob } from "../../../../services";
import {
  useRecalculateAdminSnapshotMutation,
  useRetryAdminBackfillMutation,
} from "../../../../services/admin";
import { DepartedJobNote, JobRecoveryNote, RecoveredJob } from "../types";
import { keyOfJob, recoveryMessageOf, titleOfJob } from "../utils/describeJob";

export const useJobRecovery = () => {
  const [retryBackfill] = useRetryAdminBackfillMutation();
  const [recalculateSnapshot] = useRecalculateAdminSnapshotMutation();
  const [recovered, setRecovered] = useState<Record<string, RecoveredJob>>({});
  const [isRecovering, setIsRecovering] = useState(false);

  const setNote = (job: AdminJob, note: JobRecoveryNote) =>
    setRecovered((current) => ({
      ...current,
      [keyOfJob(job)]: { job, note },
    }));

  const clearNote = (key: string) =>
    setRecovered(({ [key]: _cleared, ...others }) => others);

  const recover = async (job: AdminJob) => {
    clearNote(keyOfJob(job));
    setIsRecovering(true);
    try {
      const request =
        job.kind === "backfill" ? retryBackfill : recalculateSnapshot;
      const result = await request(job.id).unwrap();
      setNote(job, {
        tone: "unknown",
        message: recoveryMessageOf(job, result),
      });
    } catch (err: unknown) {
      setNote(job, { tone: "critical", message: toErrorMessage(err) });
    } finally {
      setIsRecovering(false);
    }
  };

  const noteOf = (job: AdminJob): JobRecoveryNote | null =>
    recovered[keyOfJob(job)]?.note ?? null;

  // A resumed snapshot leaves a filtered list, and its detail with it, so its note moves to the page.
  const departedNotesIn = (jobs: AdminJob[]): DepartedJobNote[] => {
    const listedKeys = new Set(jobs.map(keyOfJob));
    return Object.entries(recovered)
      .filter(([key]) => !listedKeys.has(key))
      .map(([key, { job, note }]) => ({
        key,
        tone: note.tone,
        message: `${titleOfJob(job)}: ${note.message}`,
      }));
  };

  return { isRecovering, noteOf, departedNotesIn, recover };
};
